import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { hasControlChars, idSchema } from '../validators'
import { COVERAGE_LABELS, type CoverageLabel, MAPPING_STATES, type MappingState } from '../vocab'

/**
 * Coverage (M4, P-COV-001, PORT-001..005, STAT-009/010). One row per latest
 * holding position, including no-concern, insufficient-information and
 * unresolved-mapping rows. Findings and labels are derived on the server.
 */

/** Engagement-state filter values (PORT-004). */
export const COVERAGE_ENGAGEMENT_STATES = ['active', 'stalled', 'closed', 'not engaged'] as const
export type CoverageEngagementState = (typeof COVERAGE_ENGAGEMENT_STATES)[number]

/** Shown instead of a finding when the holding has no verified mapping (STAT-009). */
export const NOT_YET_REVIEWED = 'Not yet reviewed'
export const UNRESOLVED_ISSUER = 'Unresolved issuer'

export const COVERAGE_SORTS = ['company', 'portfolio', 'finding', 'next_review', 'as_of'] as const
export type CoverageSort = (typeof COVERAGE_SORTS)[number]

/** `mapping=exceptions` selects every row whose mapping isn't verified. */
export const COVERAGE_MAPPING_FILTERS = ['exceptions', ...MAPPING_STATES.values] as const

const filterText = z
  .string()
  .max(180)
  .refine((v) => !hasControlChars(v), { message: 'Control characters are not permitted.' })
  .transform((v) => v.trim())

const optionalFilter = filterText.optional().transform((v) => (v ? v : undefined))

/**
 * Filters shared by `/coverage`, `/coverage/summary` and the coverage export
 * (UXR-011: preview and export use one filter implementation). Exact matches
 * except `query`, a case-insensitive match over the searchable fields (PORT-003).
 */
export const coverageFiltersSchema = z.object({
  portfolio: optionalFilter,
  finding: optionalFilter,
  theme: optionalFilter,
  country: optionalFilter,
  sector: optionalFilter,
  engagement_status: z.enum(COVERAGE_ENGAGEMENT_STATES).optional(),
  label: z.enum(COVERAGE_LABELS.values).optional(),
  mapping: z.enum(COVERAGE_MAPPING_FILTERS).optional(),
  query: optionalFilter,
})
export type CoverageFilters = z.infer<typeof coverageFiltersSchema>

/** Staff pass the client org explicitly (P-ID-013). Investors' `org` is ignored. */
export const clientContextQuerySchema = z.object({ org: idSchema.optional() })

export const coverageQuerySchema = pageQuerySchema
  .extend(clientContextQuerySchema.shape)
  .extend(coverageFiltersSchema.shape)
  .extend({
    sort: z.enum(COVERAGE_SORTS).default('company'),
    dir: z.enum(['asc', 'desc']).default('asc'),
  })
export type CoverageQuery = z.infer<typeof coverageQuerySchema>

export const coverageSummaryQuerySchema = clientContextQuerySchema.extend(
  coverageFiltersSchema.shape,
)

export type CoverageEngagementRef = {
  id: string
  title: string
  theme: string | null
  status: string
}

/** Columns only Fruuit staff receive (never investors; R-04 / P-ID-006). */
export type CoverageStaffColumns = {
  security_id: string | null
  search_run_state: string | null
  last_search_date: string | null
  scope: string | null
  mapping_source: string | null
  open_exception_id: string | null
  /** State of a newer assessment version still in the workflow, if any. */
  pending_assessment_state: string | null
}

export type CoverageRow = {
  holding_id: string
  portfolio_id: string
  portfolio: string
  as_of: string
  weight: number | null
  identifier_type: string
  identifier_value: string
  issuer_id: string | null
  /** Legal name, or "Unresolved issuer". */
  company: string
  /** The client's own issuer name from its holdings file, for mapping review. */
  issuer_name_hint: string | null
  country: string | null
  sector: string | null
  mapping_state: MappingState
  /** Published finding, "Not yet reviewed" or "Mapping <state>" (STAT-009). */
  finding: string
  priority: string | null
  assessment_id: string | null
  assessment_version: number | null
  published_at: string | null
  coverage_label: CoverageLabel
  next_review: string | null
  last_review: string | null
  engagements: CoverageEngagementRef[]
  staff?: CoverageStaffColumns
}

export type CoveragePage = {
  items: CoverageRow[]
  next_cursor: string | null
  /** Rows matching the filters (all pages). */
  total: number
  org_id: string
  /** The server clock used for "Review overdue" (D-14). */
  server_time: string
}

export type CoverageOption = {
  value: string
  label: string
}

export type CoverageSummary = {
  org_id: string
  server_time: string
  totals: {
    holdings: number
    mapped_companies: number
    mapping_exceptions: number
    portfolios: number
  }
  by_label: Partial<Record<CoverageLabel, number>>
  by_finding: Record<string, number>
  /** Filter options from the unfiltered data (PORT-004). */
  options: {
    portfolios: CoverageOption[]
    findings: string[]
    themes: string[]
    sectors: string[]
    countries: string[]
    engagement_states: CoverageEngagementState[]
    labels: CoverageLabel[]
  }
}

/** A client org a staff member can open in the workspace (P-ID-013). */
export type CoverageClient = {
  id: string
  name: string
  slug: string
  holdings: number
  open_exceptions: number
  overdue_reviews: number
}

export function isCoverageLabel(value: string): value is CoverageLabel {
  return (COVERAGE_LABELS.values as readonly string[]).includes(value)
}
